<template>
	<figure class="article-embedded-media" data-rte-video-card="true">
		<div
			class="article-embedded-media__player"
			:class="
				isNativeVideo
					? 'article-embedded-media__player--video'
					: 'article-embedded-media__player--iframe'
			"
		>
			<VideoPlayer
				v-if="isNativeVideo"
				class="article-embedded-media__native-player"
				:video-source="src"
				:poster-image="poster"
				:custom-fullscreen-handler="enableBitrixVideoSlider ? handleOpenMedia : null"
			/>

			<iframe
				v-else-if="embedSrc"
				class="article-embedded-media__iframe"
				:src="embedSrc"
				:title="resolvedTitle"
				loading="lazy"
				frameborder="0"
				allow="fullscreen; accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
				allowfullscreen
			></iframe>

			<div v-else class="article-embedded-media__empty">Видео недоступно</div>
			<button
				v-if="shouldShowOpenAction"
				type="button"
				class="article-embedded-media__action article-embedded-media__action--overlay"
				title="Открыть в окне"
				aria-label="Открыть в окне"
				@click="handleOpenMedia"
			>
				<svg
					class="article-embedded-media__action-icon"
					viewBox="0 0 20 20"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="M7.25 3.75H3.75V7.25"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M3.75 3.75L8.16667 8.16667"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M12.75 3.75H16.25V7.25"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M16.25 3.75L11.8333 8.16667"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M7.25 16.25H3.75V12.75"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M3.75 16.25L8.16667 11.8333"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M12.75 16.25H16.25V12.75"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
					<path
						d="M16.25 16.25L11.8333 11.8333"
						stroke="currentColor"
						stroke-width="1.7"
						stroke-linecap="round"
						stroke-linejoin="round"
					/>
				</svg>
				<span>Открыть в окне</span>
			</button>
		</div>

		<figcaption v-if="showCaption" class="article-embedded-media__meta">
			<span v-if="showCaption" class="article-embedded-media__caption">
				{{ resolvedTitle }}
			</span>
		</figcaption>
	</figure>
</template>

<script setup>
import { computed } from 'vue'
import VideoPlayer from '@/components/VideoPlayer.vue'
import { getVimeoEmbedUrl, getYouTubeEmbedUrl } from '@/utils/bitrixVideoSlider'

const props = defineProps({
	provider: {
		type: String,
		default: 'iframe',
	},
	src: {
		type: String,
		default: '',
	},
	title: {
		type: String,
		default: 'Видео',
	},
	poster: {
		type: String,
		default: '',
	},
	enableBitrixVideoSlider: {
		type: Boolean,
		default: false,
	},
	onOpenMedia: {
		type: Function,
		default: null,
	},
})

const resolvedTitle = computed(() => String(props.title || 'Видео').trim() || 'Видео')
const showCaption = computed(() => resolvedTitle.value && resolvedTitle.value !== 'Видео')

const isNativeVideo = computed(() =>
	['video', 'local', 'direct'].includes(String(props.provider || '').toLowerCase()),
)

const shouldShowOpenAction = computed(
	() => props.enableBitrixVideoSlider && !isNativeVideo.value && Boolean(embedSrc.value),
)

const embedSrc = computed(() => {
	const provider = String(props.provider || '').toLowerCase()

	if (provider === 'youtube') {
		return getYouTubeEmbedUrl(props.src)
	}

	if (provider === 'vimeo') {
		return getVimeoEmbedUrl(props.src)
	}

	if (provider === 'iframe') {
		return props.src || ''
	}

	return ''
})

const handleOpenMedia = () => {
	if (typeof props.onOpenMedia !== 'function') return

	props.onOpenMedia({
		url: props.src || '',
		title: resolvedTitle.value,
		poster: props.poster || '',
	})
}
</script>

<style scoped>
.article-embedded-media {
	margin: 20px 0;
}

.article-embedded-media__player {
	position: relative;
	width: 100%;
	aspect-ratio: 16 / 9;
	overflow: hidden;
	border-radius: 16px;
	background: transparent;
}

.article-embedded-media__native-player,
.article-embedded-media__iframe {
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
}

.article-embedded-media__meta {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
	margin-top: 10px;
}

.article-embedded-media__caption {
	text-align: center;
	color: #475569;
	font-size: 14px;
	line-height: 1.5;
	flex: 1 1 220px;
}

.article-embedded-media__action {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	border: 1px solid rgba(37, 99, 235, 0.18);
	border-radius: 999px;
	background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
	color: #1d4ed8;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	padding: 8px 14px;
	transition:
		background-color 0.2s ease,
		border-color 0.2s ease,
		color 0.2s ease,
		transform 0.2s ease,
		box-shadow 0.2s ease;
}

.article-embedded-media__action:hover {
	border-color: rgba(37, 99, 235, 0.28);
	background: linear-gradient(180deg, #dbeafe 0%, #bfdbfe 100%);
	box-shadow: 0 10px 24px rgba(37, 99, 235, 0.18);
	transform: translateY(-1px);
}

.article-embedded-media__action--overlay {
	position: absolute;
	right: 12px;
	bottom: 85px;
	z-index: 3;
	opacity: 0;
	visibility: hidden;
	transform: translateY(4px);
	pointer-events: none;
}

.article-embedded-media__player:hover .article-embedded-media__action--overlay,
.article-embedded-media__player:focus-within .article-embedded-media__action--overlay {
	opacity: 1;
	visibility: visible;
	transform: translateY(0);
	pointer-events: auto;
}

@media (hover: none) {
	.article-embedded-media__action--overlay {
		opacity: 1;
		visibility: visible;
		transform: translateY(0);
		pointer-events: auto;
	}
}

.article-embedded-media__action-icon {
	width: 16px;
	height: 16px;
	flex: 0 0 auto;
}

.article-embedded-media__empty {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	min-height: 220px;
	padding: 24px;
	color: #64748b;
	text-align: center;
}

@media (max-width: 640px) {
	.article-embedded-media__meta {
		justify-content: center;
	}

	.article-embedded-media__caption {
		flex-basis: 100%;
	}

	.article-embedded-media__action--overlay {
		right: 10px;
		bottom: 52px;
	}
}
</style>
